import { useMemo, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  CircularProgress,
} from '@mui/material';
import { useDateSort } from '../../../../hooks/useDateSort';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import type { Dayjs } from 'dayjs';

const RATE_BG_COLORS: { max: number; color: string }[] = [
  { max: 10, color: '#F3F7FF' },
  { max: 20, color: '#E7EFFF' },
  { max: 30, color: '#D8E4FF' },
  { max: 40, color: '#C7D7FA' },
  { max: 50, color: '#B3C8F8' },
  { max: 60, color: '#9FBAF7' },
  { max: 70, color: '#8BABF5' },
  { max: 80, color: '#769DF3' },
  { max: 90, color: '#628FF1' },
  { max: 100, color: '#4E80F0' },
];

interface RetentionEntry {
  count: number;
  ticketCount: number;
}

interface RouletteRetentionRow {
  date: string;
  newUserCount: number;
  day1: RetentionEntry;
  day2: RetentionEntry;
  day3: RetentionEntry;
  day4: RetentionEntry;
  day5: RetentionEntry;
  day6: RetentionEntry;
  day7: RetentionEntry;
}

interface RetentionDayData {
  userCount: number;
  ticketUserCount: number;
}

interface DailyRetentionRow {
  date: string;
  newUserCount: number;
  day1?: RetentionDayData;
  day2?: RetentionDayData;
  day3?: RetentionDayData;
  day4?: RetentionDayData;
  day5?: RetentionDayData;
  day6?: RetentionDayData;
  day7?: RetentionDayData;
}

interface RetentionTrendRow {
  date: string;
  newUserCount: number;
  day1?: number;
  day2?: number;
  day3?: number;
  day4?: number;
  day5?: number;
  day6?: number;
  day7?: number;
}

interface RouletteRetentionResponse {
  retentionTrend: RetentionTrendRow[];
  dailyRetention: DailyRetentionRow[];
}

interface RouletteRetentionTableProps {
  data?: RouletteRetentionRow[];
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  retentionData: RouletteRetentionResponse | null;
  loading: boolean;
}

export default function RouletteRetentionTable({
  data,
  selectedApp,
  startDate,
  endDate,
  retentionData,
  loading,
}: RouletteRetentionTableProps) {
  const tableRef = useRef<HTMLTableElement>(null);

  const tableData: RouletteRetentionRow[] = useMemo(() => {
    if (data) return data;
    if (!retentionData?.dailyRetention || retentionData.dailyRetention.length === 0) {
      return [];
    }

    return retentionData.dailyRetention.map((row) => ({
      date: row.date,
      newUserCount: row.newUserCount,
      day1: {
        count: row.day1?.userCount || 0,
        ticketCount: row.day1?.ticketUserCount || 0,
      },
      day2: {
        count: row.day2?.userCount || 0,
        ticketCount: row.day2?.ticketUserCount || 0,
      },
      day3: {
        count: row.day3?.userCount || 0,
        ticketCount: row.day3?.ticketUserCount || 0,
      },
      day4: {
        count: row.day4?.userCount || 0,
        ticketCount: row.day4?.ticketUserCount || 0,
      },
      day5: {
        count: row.day5?.userCount || 0,
        ticketCount: row.day5?.ticketUserCount || 0,
      },
      day6: {
        count: row.day6?.userCount || 0,
        ticketCount: row.day6?.ticketUserCount || 0,
      },
      day7: {
        count: row.day7?.userCount || 0,
        ticketCount: row.day7?.ticketUserCount || 0,
      },
    }));
  }, [data, retentionData]);

  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(tableData, undefined, 'asc');

  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `룰렛_리텐션_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const formatNumber = useCallback((value: number) => Math.round(value).toLocaleString('ko-KR'), []);

  const formatRetentionWithRate = useCallback(
    (count: number, ticketCount: number, newUserCount: number) => {
      const rate = newUserCount > 0 ? (count / newUserCount) * 100 : 0;
      const ticketRate = newUserCount > 0 ? (ticketCount / newUserCount) * 100 : 0;
      return {
        display: `${formatNumber(count)} (${rate.toFixed(0)}%)`,
        ticketDisplay: `${formatNumber(ticketCount)} (${ticketRate.toFixed(0)}%)`,
        rate,
      };
    },
    [formatNumber]
  );

  const getBackgroundColor = (rate: number) => {
    if (rate <= 0) return 'transparent';
    const clamped = Math.min(100, Math.round(rate));
    const found = RATE_BG_COLORS.find(({ max }) => clamped <= max);
    return found?.color ?? RATE_BG_COLORS[RATE_BG_COLORS.length - 1].color;
  };

  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((row) => [
      row.date,
      formatNumber(row.newUserCount),
      formatRetentionWithRate(row.day1.count, row.day1.ticketCount, row.newUserCount).display,
      formatRetentionWithRate(row.day2.count, row.day2.ticketCount, row.newUserCount).display,
      formatRetentionWithRate(row.day3.count, row.day3.ticketCount, row.newUserCount).display,
      formatRetentionWithRate(row.day4.count, row.day4.ticketCount, row.newUserCount).display,
      formatRetentionWithRate(row.day5.count, row.day5.ticketCount, row.newUserCount).display,
      formatRetentionWithRate(row.day6.count, row.day6.ticketCount, row.newUserCount).display,
      formatRetentionWithRate(row.day7.count, row.day7.ticketCount, row.newUserCount).display,
    ]);
  }, [sortedData, formatNumber, formatRetentionWithRate]);

  const dayKeys: Array<keyof Pick<RouletteRetentionRow, 'day1' | 'day2' | 'day3' | 'day4' | 'day5' | 'day6' | 'day7'>> = [
    'day1',
    'day2',
    'day3',
    'day4',
    'day5',
    'day6',
    'day7',
  ];

  return (
    <Box sx={{ mt: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <TableActionsMenu
              tableRef={tableRef}
              allRowsData={allRowsDataForExcel}
              filename={filename}
              sheetName="룰렛 리텐션"
            />
          </Box>

          <TableContainer
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
        <Table ref={tableRef} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  backgroundColor: '#f9fafb',
                  borderBottom: '2px solid #e5e7eb',
                }}
              >
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  backgroundColor: '#f9fafb',
                  borderBottom: '2px solid #e5e7eb',
                }}
              >
                신규 사용자 수<br />(DAY 0)
              </TableCell>
              {dayKeys.map((_, index) => (
                <TableCell
                  key={`day${index + 1}`}
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#f9fafb',
                    borderBottom: '2px solid #e5e7eb',
                  }}
                >
                  DAY {index + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData.map((row) => (
              <TableRow key={row.date}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.date}</TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {formatNumber(row.newUserCount)}
                </TableCell>
                {dayKeys.map((dayKey) => {
                  const entry = row[dayKey];
                  const formatted = formatRetentionWithRate(
                    entry.count,
                    entry.ticketCount,
                    row.newUserCount
                  );
                  const bgColor = getBackgroundColor(formatted.rate);

                  return (
                    <TableCell
                      key={dayKey}
                      align="center"
                      sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: bgColor,
                        color: formatted.rate > 50 ? '#fff' : '#111827',
                        transition: 'background-color 0.2s ease-out',
                        paddingY: 0.9,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.3,
                          fontSize: '0.9rem',
                        }}
                      >
                        <Box component="span" sx={{ lineHeight: 1.15, fontWeight: 500 }}>
                          {formatted.display}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: '0.8rem',
                            color: formatted.rate > 50 ? 'rgba(255,255,255,0.9)' : '#6b7280',
                          }}
                        >
                          {formatted.ticketDisplay}
                        </Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
